import type { ApiResponse, Category, MeResponse, Media, NewsItem, Order, Site, Work } from "./types";

declare global {
  interface Window {
    SITE_ROOT?: string;
  }
}

export function root(): string {
  const value = window.SITE_ROOT ?? "";
  return value.replace(/\/$/, "");
}

export function url(path: string): string {
  const clean = path.replace(/^\//, "");
  const base = root();
  return base ? `${base}/${clean}` : clean;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(url(path), { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) {
    throw new Error("No se pudo leer el archivo.");
  }
  return response.json() as Promise<T>;
}

let csrf = "";

export function setCsrf(token: string): void {
  csrf = token;
}

async function postJson<T>(path: string, body: unknown): Promise<ApiResponse<T>> {
  const response = await fetch(url(path), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF": csrf } : {}),
    },
    body: JSON.stringify(body),
  });
  return response.json() as Promise<ApiResponse<T>>;
}

export async function loadSite(): Promise<Site> {
  return getJson<Site>("data/site.json");
}

export async function loadWorks(): Promise<Work[]> {
  return getJson<Work[]>("data/works.json");
}

export async function loadNews(): Promise<NewsItem[]> {
  return getJson<NewsItem[]>("data/news.json");
}

export async function loadCategories(): Promise<Category[]> {
  return getJson<Category[]>("data/categories.json");
}

export async function me(): Promise<MeResponse> {
  const data = await getJson<MeResponse>("api/me.php");
  if (data.csrf) setCsrf(data.csrf);
  return data;
}

export async function login(password: string): Promise<ApiResponse> {
  const data = await postJson("api/login.php", { password });
  if (data.ok && data.csrf) setCsrf(data.csrf);
  return data;
}

export async function logout(): Promise<void> {
  await postJson("api/logout.php", {});
}

export async function saveWork(work: Work): Promise<ApiResponse<Work[]>> {
  return postJson<Work[]>("api/works.php", { action: "save", work });
}

export async function deleteWork(id: string): Promise<ApiResponse<Work[]>> {
  return postJson<Work[]>("api/works.php", { action: "delete", id });
}

export async function saveNews(item: NewsItem): Promise<ApiResponse<NewsItem[]>> {
  return postJson<NewsItem[]>("api/news.php", { action: "save", item });
}

export async function deleteNews(id: string): Promise<ApiResponse<NewsItem[]>> {
  return postJson<NewsItem[]>("api/news.php", { action: "delete", id });
}

export async function saveCategory(cat: Category): Promise<ApiResponse<Category[]>> {
  return postJson<Category[]>("api/categories.php", { action: "save", ...cat });
}

export async function deleteCategory(id: string): Promise<ApiResponse<Category[]>> {
  return postJson<Category[]>("api/categories.php", { action: "delete", id });
}

export async function saveSite(site: Site): Promise<ApiResponse<Site>> {
  return postJson<Site>("api/site.php", site);
}

export async function loadOrders(): Promise<Order[]> {
  const data = await getJson<ApiResponse<Order[]>>("api/orders.php");
  return data.data ?? [];
}

export async function deleteOrder(id: string): Promise<ApiResponse<Order[]>> {
  return postJson<Order[]>("api/orders.php", { action: "delete", id });
}

export async function sendOrder(payload: Record<string, string | string[]>): Promise<ApiResponse> {
  return postJson("api/order.php", payload);
}

export async function uploadFile(file: File): Promise<ApiResponse<Media>> {
  const body = new FormData();
  body.append("file", file);
  const response = await fetch(url("api/upload.php"), {
    method: "POST",
    credentials: "same-origin",
    headers: csrf ? { "X-CSRF": csrf } : {},
    body,
  });
  return response.json() as Promise<ApiResponse<Media>>;
}
